import React, { useMemo } from "react";
import {
	Box,
	Typography,
	Button,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Paper,
	Collapse,
	ToggleButton,
	ToggleButtonGroup,
	Alert,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { StatusBadge, formatDate } from "../../utils/functions";

// ─── Inspection History Chart Component ─────────────────────────────────────
const InspectionHistoryChart = ({
	open,
	onToggle,
	chartType,
	onChartTypeChange,
	inspections,
}) => {
	const theme = useTheme();

	console.log("inspections", inspections);

	const hasData = inspections.length > 0;

	// Group inspections by month
	const monthlyData = useMemo(() => {
		const months = {};
		inspections.forEach((insp) => {
			const month = insp.date?.slice(0, 7); // YYYY-MM
			if (!month) return;
			if (!months[month]) {
				months[month] = {
					pass: 0,
					fail: 0,
					needsAttention: 0,
					total: 0,
				};
			}
			months[month].total++;
			if (insp.overallStatus === "Pass") months[month].pass++;
			else if (insp.overallStatus === "Fail") months[month].fail++;
			else months[month].needsAttention++;
		});

		return Object.entries(months)
			.sort((a, b) => a[0].localeCompare(b[0]))
			.slice(-12) // Last 12 months
			.map(([month, data]) => ({
				month,
				label: new Date(month + "-01").toLocaleDateString("en-US", {
					month: "short",
					year: "2-digit",
				}),
				...data,
			}));
	}, [inspections]);

	// SVG dimensions
	const W = 640,
		H = 150;
	const PL = 40,
		PR = 12,
		PT = 10,
		PB = 35;
	const CW = W - PL - PR,
		CH = H - PT - PB;

	const maxTotal = Math.max(...monthlyData.map((d) => d.total), 1);
	const barWidth = Math.min(40, CW / Math.max(monthlyData.length, 1) - 8);

	return (
		<Paper sx={{ mb: 2, borderRadius: 1, overflow: "hidden" }}>
			<Box
				onClick={onToggle}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					p: 1.5,
					cursor: "pointer",
					"&:hover": { bgcolor: "action.hover" },
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="body2" fontWeight={700}>
						📊 Inspection History
					</Typography>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ fontFamily: "monospace" }}
					>
						{inspections.length} total inspection
						{inspections.length !== 1 ? "s" : ""}
					</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{open && (
						<ToggleButtonGroup
							size="small"
							value={chartType}
							exclusive
							onChange={(_, v) => v && onChartTypeChange(v)}
							onClick={(e) => e.stopPropagation()}
						>
							<ToggleButton
								value="stacked"
								sx={{ px: 1, py: 0.5 }}
							>
								☰
							</ToggleButton>
							<ToggleButton
								value="grouped"
								sx={{ px: 1, py: 0.5 }}
							>
								☲
							</ToggleButton>
						</ToggleButtonGroup>
					)}
					{open ? <ExpandLess /> : <ExpandMore />}
				</Box>
			</Box>

			<Collapse in={open}>
				<Box sx={{ p: 2, pt: 0 }}>
					{!hasData ? (
						<Box
							sx={{
								textAlign: "center",
								py: 4,
								color: "text.secondary",
							}}
						>
							No inspection history — perform inspections to see
							trends
						</Box>
					) : (
						<svg
							viewBox={`0 0 ${W} ${H}`}
							style={{
								width: "100%",
								height: "auto",
								display: "block",
							}}
						>
							{/* Grid lines */}
							{[0, 0.5, 1].map((f, i) => {
								const y = PT + CH * (1 - f);
								return (
									<g key={i}>
										<line
											x1={PL}
											y1={y}
											x2={W - PR}
											y2={y}
											stroke={theme.palette.divider}
											strokeWidth={0.6}
											strokeDasharray="3,3"
										/>
										<text
											x={PL - 4}
											y={y + 3.5}
											textAnchor="end"
											style={{
												fontSize: 8,
												fill: theme.palette.text
													.disabled,
												fontFamily: "monospace",
											}}
										>
											{Math.round(maxTotal * f)}
										</text>
									</g>
								);
							})}

							{/* Bars */}
							{monthlyData.map((d, i) => {
								const x =
									PL +
									(i / Math.max(monthlyData.length - 1, 1)) *
										CW -
									barWidth / 2;

								if (chartType === "stacked") {
									const passH = (d.pass / maxTotal) * CH;
									const failH = (d.fail / maxTotal) * CH;
									const naH =
										(d.needsAttention / maxTotal) * CH;

									return (
										<g key={d.month}>
											{/* Pass (bottom) */}
											<rect
												x={x}
												y={PT + CH - passH}
												width={barWidth}
												height={passH}
												fill={
													theme.palette.success.main
												}
												rx={2}
												opacity={0.85}
											/>
											{/* Fail (middle) */}
											<rect
												x={x}
												y={PT + CH - passH - failH}
												width={barWidth}
												height={failH}
												fill={theme.palette.error.main}
												rx={2}
												opacity={0.85}
											/>
											{/* Needs Attention (top) */}
											<rect
												x={x}
												y={
													PT +
													CH -
													passH -
													failH -
													naH
												}
												width={barWidth}
												height={naH}
												fill={
													theme.palette.warning.main
												}
												rx={2}
												opacity={0.85}
											/>
											{/* Month label */}
											<text
												x={x + barWidth / 2}
												y={H - 8}
												textAnchor="middle"
												style={{
													fontSize: 8,
													fill: theme.palette.text
														.secondary,
												}}
											>
												{d.label}
											</text>
											<title>
												{`${d.label}: ${d.total} total (${d.pass} pass, ${d.fail} fail, ${d.needsAttention} needs attention)`}
											</title>
										</g>
									);
								} else {
									// Grouped bars
									const groupWidth = barWidth / 3;
									const passH = (d.pass / maxTotal) * CH;
									const failH = (d.fail / maxTotal) * CH;
									const naH =
										(d.needsAttention / maxTotal) * CH;

									return (
										<g key={d.month}>
											<rect
												x={x}
												y={PT + CH - passH}
												width={groupWidth - 1}
												height={passH || 1}
												fill={
													theme.palette.success.main
												}
												rx={1}
												opacity={0.85}
											/>
											<rect
												x={x + groupWidth}
												y={PT + CH - failH}
												width={groupWidth - 1}
												height={failH || 1}
												fill={theme.palette.error.main}
												rx={1}
												opacity={0.85}
											/>
											<rect
												x={x + groupWidth * 2}
												y={PT + CH - naH}
												width={groupWidth - 1}
												height={naH || 1}
												fill={
													theme.palette.warning.main
												}
												rx={1}
												opacity={0.85}
											/>
											<text
												x={x + barWidth / 2}
												y={H - 8}
												textAnchor="middle"
												style={{
													fontSize: 8,
													fill: theme.palette.text
														.secondary,
												}}
											>
												{d.label}
											</text>
											<title>
												{`${d.label}: ${d.total} total (${d.pass} pass, ${d.fail} fail, ${d.needsAttention} needs attention)`}
											</title>
										</g>
									);
								}
							})}

							{/* Legend */}
							<g transform={`translate(${PL}, ${H - 8})`}>
								<rect
									x={CW - 180}
									y={-6}
									width={10}
									height={10}
									fill={theme.palette.success.main}
									rx={2}
								/>
								<text
									x={CW - 166}
									y={3}
									style={{
										fontSize: 8,
										fill: theme.palette.text.secondary,
									}}
								>
									Pass
								</text>
								<rect
									x={CW - 120}
									y={-6}
									width={10}
									height={10}
									fill={theme.palette.error.main}
									rx={2}
								/>
								<text
									x={CW - 106}
									y={3}
									style={{
										fontSize: 8,
										fill: theme.palette.text.secondary,
									}}
								>
									Fail
								</text>
								<rect
									x={CW - 60}
									y={-6}
									width={10}
									height={10}
									fill={theme.palette.warning.main}
									rx={2}
								/>
								<text
									x={CW - 46}
									y={3}
									style={{
										fontSize: 8,
										fill: theme.palette.text.secondary,
									}}
								>
									Attention
								</text>
							</g>
						</svg>
					)}
				</Box>
			</Collapse>
		</Paper>
	);
};

// ─── Enhanced Inspections Tab
const InspectionsTab = ({
	inspections,
	vehicle,
	employees,
	checklistItems,
	onAddInspection,
	onViewInspection,
	onEditInspection,
	onDeleteInspection,
	onResolveInspection,
	onCreateWorkOrder,
	inspChartOpen,
	setInspChartOpen,
	inspChartType,
	setInspChartType,
	classes,
}) => {
	// Calculate stats
	const stats = useMemo(() => {
		const passCount = inspections.filter(
			(i) => i.overallStatus === "Pass",
		).length;
		const failCount = inspections.filter(
			(i) => i.overallStatus === "Fail",
		).length;
		const needsAttentionCount = inspections.filter(
			(i) => i.overallStatus === "Needs Attention",
		).length;

		// Calculate last 30 days
		const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
		const recentInspections = inspections.filter(
			(i) => new Date(i.date) >= thirtyDaysAgo,
		);
		const recentPass = recentInspections.filter(
			(i) => i.overallStatus === "Pass",
		).length;
		const recentFail = recentInspections.filter(
			(i) => i.overallStatus === "Fail",
		).length;

		// Calculate unresolved failures
		const unresolvedFailures = inspections.filter(
			(i) =>
				(i.overallStatus === "Fail" ||
					i.overallStatus === "Needs Attention") &&
				!i.resolvedAt,
		).length;

		// Days since last inspection
		const lastInspection = inspections[0]; // Already sorted desc
		const daysSinceLastInspection = lastInspection
			? Math.floor(
					(Date.now() - new Date(lastInspection.date).getTime()) /
						(1000 * 60 * 60 * 24),
				)
			: null;

		// Pass rate
		const totalWithResult = passCount + failCount + needsAttentionCount;
		const passRate =
			totalWithResult > 0
				? Math.round((passCount / totalWithResult) * 100)
				: 0;

		return {
			passCount,
			failCount,
			needsAttentionCount,
			recentPass,
			recentFail,
			unresolvedFailures,
			daysSinceLastInspection,
			passRate,
			total: inspections.length,
		};
	}, [inspections]);

	const fmtMi = (n) => (n != null ? `${Number(n).toLocaleString()} mi` : "—");

	const getEmployeeName = (id) => {
		const emp = employees.find(
			(e) => e.value === id || e.value === String(id),
		);
		return emp ? emp.label : "—";
	};

	const getResultIcon = (status) => {
		switch (status) {
			case "Pass":
				return "✅";
			case "Fail":
				return "🚫";
			case "Needs Attention":
				return "⚠️";
			default:
				return "—";
		}
	};

	return (
		<Box>
			{/* Inspection History Chart */}
			<InspectionHistoryChart
				open={inspChartOpen}
				onToggle={() => setInspChartOpen((o) => !o)}
				chartType={inspChartType}
				onChartTypeChange={setInspChartType}
				inspections={inspections}
			/>

			{/* Header with Actions */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Typography variant="body2" color="text.secondary">
					{stats.passCount} passed · {stats.failCount} failed ·{" "}
					{stats.needsAttentionCount} needs attention
				</Typography>
				<Button variant="contained" onClick={onAddInspection}>
					+ New Inspection
				</Button>
			</Box>

			{/* Unresolved Alert */}
			{stats.unresolvedFailures > 0 && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					<Typography variant="body2" fontWeight={600}>
						{stats.unresolvedFailures} unresolved inspection
						{stats.unresolvedFailures > 1 ? "s" : ""} require
						attention
					</Typography>
					<Typography variant="caption">
						Failed inspections should be resolved by logging
						corrective actions or creating work orders.
					</Typography>
				</Alert>
			)}

			{/* Table */}
			{inspections.length === 0 ? (
				<Box className={classes.emptyState}>
					<Box className={classes.emptyIcon}>✅</Box>
					<Typography>
						No inspections recorded for this vehicle
					</Typography>
					<Button
						variant="outlined"
						onClick={onAddInspection}
						sx={{ mt: 2 }}
					>
						Create First Inspection
					</Button>
				</Box>
			) : (
				<Box className={classes.tableWrap}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>ID</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Date & Time</TableCell>
								<TableCell>Employee</TableCell>
								<TableCell>Odometer</TableCell>
								<TableCell align="center">Pass</TableCell>
								<TableCell align="center">Fail</TableCell>
								<TableCell>Result</TableCell>
								<TableCell>Resolution</TableCell>
								<TableCell>Notes</TableCell>
								{/* <TableCell align="center">Actions</TableCell> */}
							</TableRow>
						</TableHead>
						<TableBody>
							{inspections.map((insp) => {
								const passC =
									insp.responses?.filter(
										(r) => r.status === "Pass",
									).length || 0;
								const failC =
									insp.responses?.filter(
										(r) => r.status === "Fail",
									).length || 0;
								const isFailed =
									insp.overallStatus === "Fail" ||
									insp.overallStatus === "Needs Attention";
								const isResolved = !!insp.resolvedAt;

								return (
									<TableRow
										key={insp.id}
										hover
										className={classes.clickableRow}
										onClick={() => onViewInspection(insp)}
										sx={{
											bgcolor:
												isFailed && !isResolved
													? "rgba(239,68,68,0.04)"
													: "transparent",
										}}
									>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												INS-
												{String(insp.id)
													.slice(-4)
													.padStart(4, "0")}
											</Typography>
										</TableCell>
										<TableCell>
											<StatusBadge
												status={
													insp.type ||
													insp.typeInfo?.Code
												}
											/>
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												{formatDate(insp.date, true)}
											</Typography>
										</TableCell>
										<TableCell>
											{getEmployeeName(
												insp.perPersonnelId,
											)}
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												{fmtMi(insp.odometer)}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Typography
												variant="caption"
												sx={{
													fontFamily: "monospace",
													color: "success.main",
													fontWeight: 600,
												}}
											>
												{passC}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Typography
												variant="caption"
												sx={{
													fontFamily: "monospace",
													color:
														failC > 0
															? "error.main"
															: "text.disabled",
													fontWeight:
														failC > 0 ? 700 : 400,
												}}
											>
												{failC}
											</Typography>
										</TableCell>
										<TableCell>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 0.5,
												}}
											>
												{getResultIcon(
													insp.overallStatus,
												)}
												<StatusBadge
													status={insp.overallStatus}
												/>
											</Box>
										</TableCell>
										<TableCell>
											{isFailed ? (
												isResolved ? (
													<Chip
														label="Resolved"
														size="small"
														color="success"
														sx={{
															height: 20,
															fontSize: 10,
														}}
													/>
												) : (
													<Chip
														label="Unresolved"
														size="small"
														color="warning"
														sx={{
															height: 20,
															fontSize: 10,
														}}
													/>
												)
											) : (
												<Typography
													variant="caption"
													color="text.disabled"
												>
													—
												</Typography>
											)}
										</TableCell>
										<TableCell sx={{ maxWidth: 150 }}>
											<Typography
												variant="caption"
												color="text.secondary"
												noWrap
											>
												{insp.notes?.slice(0, 50)}
												{insp.notes?.length > 50
													? "…"
													: ""}
											</Typography>
										</TableCell>
										{/* <TableCell
											align="center"
											onClick={(e) => e.stopPropagation()}
										>
											<Box
												sx={{
													display: "flex",
													gap: 0.5,
													justifyContent: "center",
												}}
											>
												<Tooltip title="View Details">
													<IconButton
														size="small"
														onClick={() =>
															onViewInspection(
																insp,
															)
														}
													>
														<Visibility fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Edit">
													<IconButton
														size="small"
														onClick={() =>
															onEditInspection(
																insp,
															)
														}
													>
														<Edit fontSize="small" />
													</IconButton>
												</Tooltip>
												{isFailed && !isResolved && (
													<Tooltip title="Resolve">
														<IconButton
															size="small"
															color="warning"
															onClick={() =>
																onResolveInspection(
																	insp,
																)
															}
														>
															<CheckCircle fontSize="small" />
														</IconButton>
													</Tooltip>
												)}
												{isFailed && (
													<Tooltip title="Create Work Order">
														<IconButton
															size="small"
															color="primary"
															onClick={() =>
																onCreateWorkOrder(
																	insp,
																)
															}
														>
															<Build fontSize="small" />
														</IconButton>
													</Tooltip>
												)}
												<Tooltip title="Delete">
													<IconButton
														size="small"
														color="error"
														onClick={() =>
															onDeleteInspection(
																insp,
															)
														}
													>
														<Delete fontSize="small" />
													</IconButton>
												</Tooltip>
											</Box>
										</TableCell> */}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</Box>
			)}
		</Box>
	);
};

export default InspectionsTab;
